import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiConfig from '../config/apiConfig';
import { useAppState } from '../context/AppStateContext';
import { useCharacterUpdate } from '../hooks/useCharacterUpdate';
import './Travel.css';

const Travel = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [traveling, setTraveling] = useState(false);
  const [travelTime, setTravelTime] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [cooldownTime, setCooldownTime] = useState(null);
  
  const navigate = useNavigate();
  const { updateAfterAction } = useCharacterUpdate();
  const { character, updateTravelCooldown } = useAppState();

  // Fetch cities on component mount
  useEffect(() => {
    const fetchCities = async () => {
      try {
        // Check if we have an active cooldown
        const storedCooldown = localStorage.getItem('travel_cooldown');
        if (storedCooldown) {
          const cooldownEnd = new Date(storedCooldown);
          const now = new Date();
          
          if (cooldownEnd > now) {
            // Cooldown is still active
            setCooldownTime(cooldownEnd);
            
            // Show cooldown message
            const cooldownMinutes = Math.ceil((cooldownEnd - now) / (60 * 1000));
            setError({
              type: 'warning',
              message: `You need to rest for ${cooldownMinutes} more minutes before traveling again.`
            });
          } else {
            // Cooldown expired, clear it
            localStorage.removeItem('travel_cooldown');
          }
        }
        
        setLoading(true);
        
        // First, get current character data to ensure we have the most up-to-date location
        const characterResponse = await fetch(apiConfig.getCharacter, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!characterResponse.ok) {
          throw new Error(`Failed to fetch character data: ${characterResponse.status}`);
        }
        
        const characterData = await characterResponse.json();
        console.log('Fresh character data:', characterData);
        
        // Get current location details
        let currentCityId = null;
        
        if (characterData.location) {
          if (typeof characterData.location === 'object' && characterData.location.id) {
            currentCityId = characterData.location.id;
            setCurrentLocation(characterData.location);
          } else if (typeof characterData.location === 'string') {
            currentCityId = characterData.location;
            // Need to fetch location details for display
            try {
              const cityResponse = await fetch(apiConfig.city(currentCityId), {
                method: 'GET',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json'
                }
              });
              
              if (cityResponse.ok) {
                const cityData = await cityResponse.json();
                setCurrentLocation(cityData);
              }
            } catch (cityErr) {
              console.error('Error fetching current city details:', cityErr);
            }
          }
        }
        
        console.log('Current city ID determined to be:', currentCityId);
        
        // Now fetch all cities
        const citiesResponse = await fetch(apiConfig.cities, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!citiesResponse.ok) {
          throw new Error(`Failed to fetch cities: ${citiesResponse.status}`);
        }

        const citiesData = await citiesResponse.json();
        console.log(`Fetched ${citiesData.length} total cities`);
        
        // Explicitly remove the current city
        const filteredCities = currentCityId 
          ? citiesData.filter(city => city.id !== currentCityId)
          : citiesData;
        
        console.log(`After filtering, ${filteredCities.length} cities remain`);
        
        // Double-check if any city with the same ID as current city remains
        const remainingCurrentCity = filteredCities.find(city => 
          city.id === currentCityId || 
          (characterData.location && city.name === characterData.location.name)
        );
        
        if (remainingCurrentCity) {
          console.error('Current city still in list after filtering:', remainingCurrentCity);
          // Remove it manually as a last resort
          const manuallyFiltered = filteredCities.filter(city => city.id !== remainingCurrentCity.id);
          setCities(manuallyFiltered);
        } else {
          setCities(filteredCities);
        }
      } catch (err) {
        console.error('Error fetching cities:', err);
        setError(err.message || 'Failed to load travel destinations. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCities();
  }, [character]);

  // Begin travel to selected city using the API endpoint
  const beginTravel = async () => {
    if (!selectedCity) return;
    
    try {
      setTraveling(true);
      setError(null);
      
      console.log(`Traveling to city ID: ${selectedCity.id}`);
      
      // Fix the parameter name from cityId to cityID - IMPORTANT
      const response = await fetch(apiConfig.travelCity, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cityID: selectedCity.id }) // Changed from cityId to cityID
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Travel failed: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Travel successful:', data);
      
      // Handle cooldown if present in the response
      if (data.cooldown) {
        try {
          // Parse the cooldown datetime
          const cooldownTime = new Date(data.cooldown);
          console.log(`Travel cooldown until: ${cooldownTime.toLocaleString()}`);
          
          // Store cooldown in localStorage for persistence
          localStorage.setItem('travel_cooldown', cooldownTime.toISOString());
          
          // Update the local state
          setCooldownTime(cooldownTime);
          
          // Only call updateTravelCooldown if it exists in context
          if (typeof updateTravelCooldown === 'function') {
            updateTravelCooldown(cooldownTime);
          }
        } catch (cooldownErr) {
          console.error('Error parsing cooldown time:', cooldownErr);
        }
      }
      
      // Update character location with the returned data
      if (updateAfterAction) {
        // Only update if we have location data in the response
        if (data && data.location) {
          updateAfterAction({
            ...character,
            location: data.location
          });
        } else {
          // If API doesn't return location, use selected city
          updateAfterAction({
            ...character,
            location: selectedCity
          });
        }
      }
      
      // Show success message before navigating
      // Include cooldown information if available
      if (data.cooldown) {
        const cooldownTime = new Date(data.cooldown);
        const now = new Date();
        const cooldownMinutes = Math.ceil((cooldownTime - now) / (60 * 1000));
        
        setError({ 
          type: 'success', 
          message: `You have arrived at ${selectedCity.name}! You'll need to rest for ${cooldownMinutes} minutes before traveling again.` 
        });
      } else {
        setError({ type: 'success', message: `You have arrived at ${selectedCity.name}!` });
      }
      
      // Navigate to city page after a brief delay to show the success message
      setTimeout(() => {
        navigate('/city');
      }, 1500);
      
    } catch (err) {
      console.error('Error traveling to city:', err);
      setError(err.message || 'Failed to travel to destination. Please try again later.');
    } finally {
      setTraveling(false);
    }
  };

  // Calculate travel time between locations
  const calculateTravelTime = (currentCity, destinationCity) => {
    // Default time if coordinates aren't available
    let time = 60;
    
    // If we have coordinates for both cities, calculate distance-based travel time
    if (currentCity && destinationCity && 
        typeof currentCity.xPosition === 'number' && 
        typeof currentCity.yPosition === 'number' && 
        typeof destinationCity.xPosition === 'number' && 
        typeof destinationCity.yPosition === 'number') {
      
      // Calculate Manhattan distance (number of grid squares in the most direct route)
      const dx = Math.abs(destinationCity.xPosition - currentCity.xPosition);
      const dy = Math.abs(destinationCity.yPosition - currentCity.yPosition);
      const gridDistance = dx + dy;
      
      // 20 minutes per grid square
      time = gridDistance * 20;
      
      console.log(`Travel calculation: ${currentCity.name} to ${destinationCity.name} - ${gridDistance} squares = ${time} minutes`);
    } else {
      // Fallback if coordinates aren't available
      console.log('Missing coordinates for travel time calculation, using fallback method');
      
      if (typeof destinationCity.alignment === 'number' && typeof currentCity?.alignment === 'number') {
        // Larger alignment difference = longer travel time
        const alignmentDiff = Math.abs(destinationCity.alignment - currentCity.alignment);
        time += Math.floor(alignmentDiff / 5);
      }
      
      // Add some variation
      time += Math.floor(Math.random() * 20) + 10;
    }
    
    // Ensure there's always some minimum travel time
    return Math.max(20, time);
  };

  // Handle city selection
  const selectDestination = (city) => {
    setSelectedCity(city);
    const time = calculateTravelTime(currentLocation, city);
    setTravelTime(time);
  };

  // Get city size display
  const getCitySize = (city) => {
    // Use buildings array length if available as a proxy for size
    const buildingCount = city.buildings?.length || 0;
    
    if (buildingCount > 10) return 'Metropolis';
    if (buildingCount > 7) return 'Large City';
    if (buildingCount > 5) return 'City';
    if (buildingCount > 3) return 'Small City';
    if (buildingCount > 1) return 'Town';
    return 'Village';
  };

  // Get city icon based on alignment
  const getCityIcon = (city) => {
    const alignment = city.alignment;
    
    if (alignment === undefined || alignment === null) return '🏙️';
    if (alignment > 30) return '✨';
    if (alignment > 10) return '🏰';
    if (alignment >= 0) return '🏘️'; // This correctly handles 0
    if (alignment > -10) return '🏚️';
    if (alignment > -30) return '🏴';
    return '💀';
  };

  // Format travel time
  const formatTravelTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes} minutes`;
    } else {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours} hour${hours > 1 ? 's' : ''} ${mins > 0 ? `${mins} minutes` : ''}`;
    }
  };

  // Get city alignment text
  const getCityAlignmentText = (alignment) => {
    if (alignment === undefined || alignment === null) return 'Neutral';
    if (alignment > 30) return 'Virtuous';
    if (alignment > 10) return 'Good';
    if (alignment > 0) return 'Slightly Good';
    if (alignment === 0) return 'Neutral';
    if (alignment > -10) return 'Slightly Evil';
    if (alignment > -30) return 'Evil';
    return 'Wicked';
  };

  return (
    <div className="page-content travel-container">
      <h2>Travel to New Lands</h2>
      
      {currentLocation && (
        <div className="current-location">
          <h3>Current Location</h3>
          <div className="location-card">
            <span className="location-icon">{getCityIcon(currentLocation)}</span>
            <div className="location-info">
              <span className="location-name">{currentLocation.name}</span>
              <span className="location-type">{getCitySize(currentLocation)}</span>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className={`message-box ${error.type === 'success' ? 'success-message' : 'error-message'}`}>
          <p>{error.message || error}</p>
          {error.type !== 'success' && (
            <button onClick={() => setError(null)}>Dismiss</button>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading available destinations...</p>
        </div>
      ) : (
        <>
          <div className="travel-content">
            <div className="destinations-list">
              <h3>Available Destinations</h3>
              {cities.length > 0 ? (
                <ul>
                  {cities.map(city => (
                    <li 
                      key={city.id} 
                      className={selectedCity?.id === city.id ? 'selected' : ''}
                      onClick={() => selectDestination(city)}
                    >
                      <span className="city-icon">{getCityIcon(city)}</span>
                      <div className="city-list-info">
                        <span className="city-name">{city.name}</span>
                        <span className="city-size">{getCitySize(city)}</span>
                      </div>
                      <span className="travel-arrow">→</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="no-destinations">
                  <p>No available destinations found.</p>
                </div>
              )}
            </div>

            <div className="travel-details">
              {selectedCity ? (
                <div className="destination-detail">
                  <div className="destination-header">
                    <h3>Destination: {selectedCity.name}</h3>
                    <div className="city-badge">{getCitySize(selectedCity)}</div>
                  </div>
                  
                  <div className="city-image">
                    <div className="city-icon-large">{getCityIcon(selectedCity)}</div>
                  </div>
                  
                  <div className="travel-info">
                    <div className="travel-stat">
                      <span className="stat-label">Estimated Travel Time:</span>
                      <span className="stat-value">{formatTravelTime(travelTime || 0)}</span>
                    </div>
                    
                    <div className="travel-stat">
                      <span className="stat-label">Coordinates:</span>
                      <span className="stat-value">
                        X: {selectedCity.xPosition !== undefined ? selectedCity.xPosition : '?'}, 
                        Y: {selectedCity.yPosition !== undefined ? selectedCity.yPosition : '?'}
                      </span>
                    </div>
                    
                    <div className="travel-stat">
                      <span className="stat-label">Alignment:</span>
                      <span className="stat-value">{getCityAlignmentText(selectedCity.alignment)}</span>
                    </div>
                    
                    {selectedCity.owner && (
                      <div className="travel-stat">
                        <span className="stat-label">Ruler:</span>
                        <span className="stat-value">{selectedCity.ownerName || 'Unknown'}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="travel-description">
                    <p>{selectedCity.description || 'No information available about this destination.'}</p>
                  </div>
                  
                  <div className="travel-actions">
                    <button 
                      className="travel-btn" 
                      onClick={beginTravel}
                      disabled={traveling || cooldownTime > new Date()}
                    >
                      {traveling ? 'Traveling...' : cooldownTime > new Date() ? 'Resting...' : 'Begin Journey'}
                    </button>
                  </div>

                  {cooldownTime && cooldownTime > new Date() && (
                    <div className="cooldown-indicator">
                      <div className="cooldown-icon">⏳</div>
                      <div className="cooldown-text">
                        <p>Travel cooldown active</p>
                        <p>Ready at: {cooldownTime.toLocaleTimeString()}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="travel-placeholder">
                  <p>Select a destination to begin your journey</p>
                  <div className="travel-placeholder-icon">🧭</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Travel;
